import { useQuery } from "@tanstack/react-query"

import { type Customization, getCustomizations } from "@/api/customization"
import { queryKeys } from "@/queries/keys"

// The org's default customization ships inline in bootstrap viewData and is
// seeded into the query cache by `hydrateQueriesFromViewData` before any route
// mounts. The backend only packs it when the org can read `Customization`, and
// that same permission gates `getCustomizations`, so an unseeded render has no
// eager fetch to fall back to — the query stays disabled and consumers read the
// hydrated snapshot directly, treating "no default customization" as "use the
// built-in defaults" (matching the pre-RQ bootstrap read). The queryFn exists so
// the settings surface can opt into refetching by enabling the query.
export function useDefaultCustomization(): Customization | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.customizations.default,
    async queryFn() {
      const result = await getCustomizations()
      if (result.isErr) {
        throw new Error(result.error.message, { cause: result.error })
      }
      return result.value.find((customization) => customization.isDefault) ?? null
    },
    enabled: false,
    staleTime: Infinity,
  })
  return data ?? undefined
}
